import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  CompanyBasketsDataSource,
  CompanyProductPayload,
  CompanySearchItem,
  CompanySettings,
} from '../../../../datasources/companyBaskets/CompanyBasketsDataSource';
import {
  ProductFlowLink,
  ProductFlowsDataSource,
} from '../../../../datasources/productFlows/ProductFlowsDataSource';
import { FlowsDataSource } from '../../../../datasources/flows/FlowsDataSource';
import {
  getApiErrorMessage,
  getGenericErrorMessage,
} from '../../../../datasources/GenericErrors';
import { getProducts } from '../../../../services/products/productService';
import { Flow } from '../../../../types/flow/Flow';
import { BasketListScreen } from '../shared/BasketListScreen';
import { BasketTable, BasketTableColumn } from '../shared/BasketTable';
import { useTextSearch } from '../shared/useTextSearch';

type CatalogProduct = {
  id: number;
  sap_code: string;
  description: string;
  category: string;
  subcategory: string;
};

const ALL_CATEGORIES = '';

type PortfolioFilter = 'all' | 'hidden' | 'visible';

const PORTFOLIO_FILTERS: { value: PortfolioFilter; label: string }[] = [
  { value: 'all', label: 'Todos os produtos' },
  { value: 'hidden', label: 'Somente ocultos' },
  { value: 'visible', label: 'Somente visíveis' },
];

type CompanyBasketsPageProps = {
  /** CNPJ escolhido, mantido pela tela pai ao trocar de aba. */
  initialCnpj?: string;
  onCompanyChange?: (cnpj: string | undefined) => void;
  /** Abre a aba Fluxos destacando o fluxo. */
  onOpenFlow?: (flowId: number) => void;
};

function formatCnpj(cnpj: string): string {
  const digits = String(cnpj ?? '').replace(/\D/g, '');
  return digits.length === 14
    ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    : cnpj;
}

/** Mesma lista de produtos da implantação: árvore Categoria → Subcategoria → produtos. */
function flattenCatalog(tree: unknown): CatalogProduct[] {
  if (!Array.isArray(tree)) return [];
  const products = new Map<string, CatalogProduct>();

  tree.forEach((category: any) => {
    (category?.Sub_categorias ?? []).forEach((subcategory: any) => {
      (subcategory?.produtos ?? []).forEach((product: any) => {
        const sapCode = String(product?.sap_code ?? '').trim();
        if (!sapCode || products.has(sapCode)) return;
        products.set(sapCode, {
          id: Number(product.id ?? product.cod_prod),
          sap_code: sapCode,
          description: String(
            product.description ?? product['Descrição do Item '] ?? ''
          ).trim(),
          category: String(category.Categoria ?? '').trim(),
          subcategory: String(subcategory.description ?? '').trim(),
        });
      });
    });
  });

  return Array.from(products.values()).sort((first, second) =>
    first.description.localeCompare(second.description)
  );
}

/**
 * Configuração por empresa: produtos ocultos do portfólio e fluxo alternativo
 * de cada produto. O fluxo principal continua na aba Produtos.
 */
export const CompanyBasketsPage = ({
  initialCnpj,
  onCompanyChange,
  onOpenFlow,
}: CompanyBasketsPageProps) => {
  const [catalog, setCatalog] = useState<CatalogProduct[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [productLinks, setProductLinks] = useState<ProductFlowLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [retry, setRetry] = useState(0);

  const [company, setCompany] = useState<CompanySearchItem | null>(null);
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [busySapCode, setBusySapCode] = useState<string>();
  const [resetting, setResetting] = useState(false);
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [portfolioFilter, setPortfolioFilter] =
    useState<PortfolioFilter>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      getProducts(undefined),
      FlowsDataSource.getAll(),
      ProductFlowsDataSource.getAll(),
    ])
      .then(([tree, flowData, linkData]) => {
        if (cancelled) return;
        setCatalog(flattenCatalog(tree));
        setFlows(flowData);
        setProductLinks(linkData);
        setError(undefined);
      })
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setError(
            getGenericErrorMessage(
              loadError,
              'Não foi possível carregar os produtos e os fluxos.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [retry]);

  useEffect(() => {
    if (!initialCnpj) return;
    let cancelled = false;

    setLoadingCompany(true);
    CompanyBasketsDataSource.getCompany(initialCnpj)
      .then((loaded) => {
        if (cancelled) return;
        setSettings(loaded);
        setCompany({
          id: loaded.company.id,
          cnpj: loaded.company.cnpj,
          empresa: loaded.company.name,
          portfolioType: loaded.portfolioType,
          productsCount: loaded.hiddenCount,
        });
      })
      .catch(() => {
        // Empresa não carregou ao voltar para a aba: a busca continua disponível.
        if (!cancelled) onCompanyChange?.(undefined);
      })
      .finally(() => {
        if (!cancelled) setLoadingCompany(false);
      });

    return () => {
      cancelled = true;
    };
    // Restaura a empresa só ao montar a aba.
  }, []);

  async function selectCompany(next: CompanySearchItem | null): Promise<void> {
    setCompany(next);
    setSettings(null);
    onCompanyChange?.(next?.cnpj);
    if (!next) return;

    setLoadingCompany(true);
    try {
      setSettings(await CompanyBasketsDataSource.getCompany(next.cnpj));
      setError(undefined);
    } catch (loadError) {
      setError(
        getApiErrorMessage(loadError, 'Não foi possível carregar a empresa.')
      );
    } finally {
      setLoadingCompany(false);
    }
  }

  async function saveProduct(payload: CompanyProductPayload): Promise<void> {
    if (!settings) return;
    setBusySapCode(payload.sap_code);
    try {
      setSettings(
        await CompanyBasketsDataSource.saveProduct(
          settings.company.cnpj,
          payload
        )
      );
      setError(undefined);
    } catch (saveError) {
      setError(
        getApiErrorMessage(saveError, 'Não foi possível salvar o produto.')
      );
    } finally {
      setBusySapCode(undefined);
    }
  }

  async function resetPortfolio(): Promise<void> {
    if (!settings) return;
    if (
      !window.confirm(
        `Mostrar todos os produtos para ${settings.company.name}? Os produtos ocultos voltam ao portfólio.`
      )
    ) {
      return;
    }

    setResetting(true);
    try {
      await CompanyBasketsDataSource.resetPortfolio(settings.company.cnpj);
      setSettings(
        await CompanyBasketsDataSource.getCompany(settings.company.cnpj)
      );
      setError(undefined);
    } catch (resetError) {
      setError(
        getApiErrorMessage(
          resetError,
          'Não foi possível mostrar todos os produtos.'
        )
      );
    } finally {
      setResetting(false);
    }
  }

  const categories = useMemo(
    () =>
      Array.from(new Set(catalog.map((product) => product.category)))
        .filter(Boolean)
        .sort((first, second) => first.localeCompare(second)),
    [catalog]
  );
  const mainFlowBySap = useMemo(
    () =>
      new Map(
        productLinks
          .filter((link) => link.flow)
          .map((link) => [link.product_sap_code, link.flow!])
      ),
    [productLinks]
  );
  const hiddenSap = useMemo(
    () =>
      new Set(
        (settings?.products ?? [])
          .filter((product) => product.hidden)
          .map((product) => product.sap_code)
      ),
    [settings]
  );
  const settingBySap = useMemo(
    () =>
      new Map(
        (settings?.products ?? []).map((product) => [product.sap_code, product])
      ),
    [settings]
  );
  const activeFlows = flows.filter((flow) => flow.active !== false);

  const columns: BasketTableColumn<CatalogProduct>[] = [
    {
      id: 'sap',
      label: 'Código SAP',
      minWidth: 110,
      render: (product) => product.sap_code,
    },
    {
      id: 'product',
      label: 'Produto',
      minWidth: 280,
      render: (product) => (
        <>
          <Typography variant="body2" fontWeight={700}>
            {product.description || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[product.category, product.subcategory]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </>
      ),
    },
    {
      id: 'mainFlow',
      label: 'Fluxo principal',
      minWidth: 150,
      render: (product) => {
        const flow = mainFlowBySap.get(product.sap_code);
        if (!flow) return '—';
        return (
          <Link
            component="button"
            type="button"
            underline="hover"
            variant="body2"
            title="Abrir este fluxo na aba Fluxos"
            aria-label={`Abrir o fluxo ${flow.code} na aba Fluxos`}
            onClick={() => onOpenFlow?.(flow.id)}
            sx={{ fontWeight: 600 }}
          >
            {flow.code}
          </Link>
        );
      },
    },
    {
      id: 'hidden',
      label: 'Ocultar do portfólio',
      minWidth: 140,
      align: 'center',
      render: (product) => (
        <Switch
          size="small"
          checked={hiddenSap.has(product.sap_code)}
          disabled={busySapCode === product.sap_code || resetting}
          inputProps={{
            'aria-label': `${product.sap_code} oculto no portfólio`,
          }}
          onChange={(event) =>
            void saveProduct({
              sap_code: product.sap_code,
              hidden: event.target.checked,
            })
          }
        />
      ),
    },
    {
      id: 'alternativeFlow',
      label: 'Fluxo alternativo',
      minWidth: 220,
      render: (product) => {
        const current = settingBySap.get(product.sap_code)?.alternative_flow_id;
        const selected =
          current == null
            ? null
            : activeFlows.find((flow) => flow.id === current) ?? null;
        const missing = current != null && selected === null;
        return (
          <Autocomplete
            size="small"
            fullWidth
            value={selected}
            options={activeFlows}
            disabled={busySapCode === product.sap_code}
            getOptionLabel={(flow) => flow.code}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="Nenhum fluxo encontrado"
            clearText="Usar o principal"
            onChange={(_, flow) =>
              void saveProduct({
                sap_code: product.sap_code,
                alternative_flow_id: flow ? flow.id : null,
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Usar o principal"
                error={missing}
                helperText={missing ? 'Fluxo excluído ou inativo' : undefined}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': `Fluxo alternativo de ${product.sap_code}`,
                }}
              />
            )}
          />
        );
      },
    },
  ];

  return (
    <BasketListScreen
      title="Empresas"
      description="Defina, por CNPJ, os produtos ocultos do portfólio e o fluxo alternativo de cada produto."
      searchPlaceholder="Buscar produto por código SAP ou descrição..."
      loading={loading}
      loadingLabel="Carregando produtos"
      error={error}
      onRetry={() => setRetry((attempt) => attempt + 1)}
      notice={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <CompanyPicker
            value={company}
            onChange={(next) => void selectCompany(next)}
          />
          {loadingCompany && <LinearProgress />}
          {settings && (
            <CompanySummary
              settings={settings}
              resetting={resetting}
              onReset={() => void resetPortfolio()}
            />
          )}
        </div>
      }
    >
      {(search) =>
        settings ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label="Categoria"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                style={{ minWidth: 260 }}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value={ALL_CATEGORIES}>
                  <em>Todas as categorias</em>
                </MenuItem>
                {categories.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Portfólio"
                value={portfolioFilter}
                onChange={(event) =>
                  setPortfolioFilter(event.target.value as PortfolioFilter)
                }
                style={{ minWidth: 220 }}
                InputLabelProps={{ shrink: true }}
              >
                {PORTFOLIO_FILTERS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <CompanyProductsTable
              catalog={catalog}
              category={category}
              portfolioFilter={portfolioFilter}
              hiddenSap={hiddenSap}
              search={search}
              columns={columns}
            />
          </div>
        ) : (
          <Paper
            variant="outlined"
            style={{ padding: 24, textAlign: 'center', borderStyle: 'dashed' }}
          >
            <Typography color="text.secondary">
              Pesquise uma empresa para ver o portfólio e os fluxos
              alternativos.
            </Typography>
          </Paper>
        )
      }
    </BasketListScreen>
  );
};

type CompanyPickerProps = {
  value: CompanySearchItem | null;
  onChange: (company: CompanySearchItem | null) => void;
};

const CompanyPicker = ({ value, onChange }: CompanyPickerProps) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<CompanySearchItem[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = input.trim();
    if (term.length < 2) {
      setOptions(value ? [value] : []);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setSearching(true);
      CompanyBasketsDataSource.searchCompanies(term)
        .then((data) => {
          if (!cancelled) setOptions(data);
        })
        .catch(() => {
          if (!cancelled) setOptions([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [input, value]);

  return (
    <Autocomplete
      value={value}
      options={options}
      loading={searching}
      filterOptions={(items) => items}
      isOptionEqualToValue={(option, selected) => option.cnpj === selected.cnpj}
      getOptionLabel={(option) =>
        `${option.empresa} — ${formatCnpj(option.cnpj)}`
      }
      onChange={(_, next) => onChange(next)}
      onInputChange={(_, next) => setInput(next)}
      noOptionsText={
        input.trim().length < 2
          ? 'Digite ao menos 2 caracteres do CNPJ ou do nome'
          : 'Nenhuma empresa encontrada'
      }
      renderOption={(props, option) => (
        <li {...props} key={option.cnpj}>
          <div>
            <div>{option.empresa}</div>
            <Typography variant="caption" color="text.secondary">
              {formatCnpj(option.cnpj)}
            </Typography>
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Pesquisar empresa"
          placeholder="CNPJ ou nome da empresa"
        />
      )}
    />
  );
};

type CompanySummaryProps = {
  settings: CompanySettings;
  resetting: boolean;
  onReset: () => void;
};

const CompanySummary = ({
  settings,
  resetting,
  onReset,
}: CompanySummaryProps) => {
  const restricted = settings.portfolioType === 'RESTRITO';

  return (
    <Paper
      variant="outlined"
      style={{
        padding: 16,
        borderRadius: 12,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div>
        <Typography variant="subtitle1" fontWeight={700}>
          {settings.company.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          CNPJ: {formatCnpj(settings.company.cnpj)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {restricted
            ? 'Os produtos ocultos não aparecem no portfólio desta empresa.'
            : 'Portfólio geral: todos os produtos aparecem. Marque um produto para ocultá-lo.'}
        </Typography>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Chip
          color={restricted ? 'warning' : 'success'}
          label={
            restricted
              ? `${settings.hiddenCount} produto(s) oculto(s)`
              : 'Portfólio geral'
          }
        />
        {restricted && (
          <Button
            size="small"
            variant="outlined"
            onClick={onReset}
            disabled={resetting}
          >
            Mostrar todos os produtos
          </Button>
        )}
      </div>
      <Alert severity="info" style={{ width: '100%' }}>
        Os produtos ocultos ainda não são removidos do catálogo do pedido. O
        fluxo alternativo já vale no pedido para os usuários desta empresa.
      </Alert>
    </Paper>
  );
};

type CompanyProductsTableProps = {
  catalog: CatalogProduct[];
  category: string;
  portfolioFilter: PortfolioFilter;
  hiddenSap: Set<string>;
  search: string;
  columns: BasketTableColumn<CatalogProduct>[];
};

const CompanyProductsTable = ({
  catalog,
  category,
  portfolioFilter,
  hiddenSap,
  search,
  columns,
}: CompanyProductsTableProps) => {
  const inCategory = useMemo(
    () =>
      catalog.filter(
        (product) =>
          (category === ALL_CATEGORIES || product.category === category) &&
          (portfolioFilter === 'all' ||
            hiddenSap.has(product.sap_code) === (portfolioFilter === 'hidden'))
      ),
    [catalog, category, portfolioFilter, hiddenSap]
  );
  const rows = useTextSearch(inCategory, search, (product) => [
    product.sap_code,
    product.description,
    product.subcategory,
  ]);

  return (
    <BasketTable
      columns={columns}
      rows={rows}
      getRowId={(product) => product.sap_code}
      getRowLabel={(product) => `produto ${product.sap_code}`}
      itemsLabel="produtos"
      emptyMessage={
        portfolioFilter === 'hidden'
          ? 'Nenhum produto oculto para esta empresa.'
          : 'Nenhum produto da implantação nesta categoria.'
      }
      searching={search.trim() !== ''}
      resetKey={`${category}|${portfolioFilter}|${search}`}
    />
  );
};
